import { Branded }		from "ts-base/branded";
import { Fn }			from "ts-base/fn";
import { NotUndefined }	from "ts-base/notUndefined";
import { Numbers }		from "ts-base/numbers";
import { Equal }		from "ts-base/equal";
import { Comparator }	from "ts-base/comparator";
import { Group }		from "ts-base/group";
import { Numeric }		from "ts-base/numeric/numeric";

/** millisecond duration */
export type Duration	= Branded<number, "Duration">;

export namespace Duration {
	export const name		= "Duration";

	export const brand		= Branded.brand<Duration>;
	export const is			= (it:number):it is Duration	=> Number.isInteger(it);

	export const fromMillis		= (it:number):Duration	=> brand(Math.round(it));
	export const fromSeconds	= (it:number):Duration	=> fromMillis(it * 1000);
	export const fromMinutes	= (it:number):Duration	=> fromSeconds(it * 60);
	export const fromHours		= (it:number):Duration	=> fromMinutes(it * 60);
	export const fromDays		= (it:number):Duration	=> fromHours(it * 24);

	export const toMillis	= (it:Duration):number	=> it;
	export const toSeconds	= (it:Duration):number	=> toMillis(it)		/ 1000;
	export const toMinutes	= (it:Duration):number	=> toSeconds(it)	/ 60;
	export const toHours	= (it:Duration):number	=> toMinutes(it)	/ 60;
	export const toDays		= (it:Duration):number	=> toHours(it)		/ 24;

	export const fromUnitString	= (it:string):Duration|null	=> {
		const m	= /^(?<value>(0|[1-9][0-9]*)([.][0-9]+)?)\s*(?<unit>d|h|m|s|ms)$/.exec(it);
		if (m === null)	return null;

		const groups	= NotUndefined.unsafe(m.groups);

		const unit		= NotUndefined.unsafe(groups["unit"]);
		const builder	=
			unit === "d"	? fromDays		:
			unit === "h"	? fromHours		:
			unit === "m"	? fromMinutes	:
			unit === "s"	? fromSeconds	:
			unit === "ms"	? fromMillis	:
			Fn.never();

		const num	= NotUndefined.unsafe(groups["value"]);
		const value	= parseFloat(num);

		return builder(value);
	};

	export const fromNumber	= fromMillis;
	export const toNumber	= toMillis;

	export const zero		= brand(0);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-unary-minus
	export const negate		= (it:Duration):Duration			=> brand(-it);
	export const add		= (a:Duration, b:Duration):Duration	=> brand(a + b);
	export const subtract	= (a:Duration, b:Duration):Duration	=> brand(a - b);

	export const scaleUp	= (a:Duration, b:number):Duration	=> fromMillis(a * b);
	export const scaleDown	= (a:Duration, b:number):Duration	=> fromMillis(a / b);

	export const equal:Equal<Duration>			= Numbers.equal;
	export const compare:Comparator<Duration>	= Numbers.compare;
	export const sum:Group<Duration>			= { empty: zero,	concat: add,	inverse: negate,	concatInverse: subtract	};
}

// just for type checking
Numeric.typed(Duration);
